import { db } from "@/main";
import {
  getIresDeviceCategoryFromSerialNumber,
  getIresDeviceCollectionName,
  IresDeviceCategory,
} from "@/data/models/IresDevice";
import { HyDryEventLog, HyDryEventLogNote } from "@/data/models/HyDryEventLog";
import { QuerySnapshot } from "@firebase/firestore-types";
import {
  getHyDryEventFromEventNumber,
  HyDryEvent,
  HyDryEventCategory,
  HyDryEventMessage,
  resolveEffectiveHyDryEventCategory,
  UnreadHyDryEventMessagesInfo,
} from "../models/HyDryEvent";
import { firestore } from "firebase";
import {
  doesProgramChainIncludeActiveRun,
  getEndDateOfProgramChain,
  ProgramChain,
} from "../models/ProgramChain";
import { filterOutUndefined } from "@/utils/Utils";

function getHyDryEventLogsRef(serialNumber: string) {
  return db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("hyDryEventLogs");
}

function sortValueForHyDryEvent(hyDryEvent: HyDryEvent): number {
  switch (hyDryEvent) {
    case HyDryEvent.DRYING_PROGRAM_STARTED:
      return 1;
    case HyDryEvent.DRYING_STARTED:
      return -2;
    case HyDryEvent.PROGRAM_PHASE_ENDED:
      return -3;
    case HyDryEvent.DRYING_PROGRAM_DONE:
    case HyDryEvent.DRYING_PROGRAM_TERMINATED:
      return -1;
    case HyDryEvent.DRYING_ENDED:
      return 1;

    default:
      return 0;
  }
}

export function compareHyDryEvents(
  aEvent: HyDryEvent,
  aEventTimestamp: number,
  aCreatedAt: number,

  bEvent: HyDryEvent,
  bEventTimestamp: number,
  bCreatedAt: number
) {
  const eventDateDelta = bEventTimestamp - aEventTimestamp;

  if (eventDateDelta !== 0 || !aCreatedAt || !bCreatedAt) {
    return eventDateDelta;
  } else {
    const createdAtDelta = bCreatedAt - aCreatedAt;

    if (createdAtDelta === 0) {
      return sortValueForHyDryEvent(bEvent) - sortValueForHyDryEvent(aEvent);
    } else {
      return createdAtDelta;
    }
  }
}

export function compareHyDryEventMessages(
  a: HyDryEventMessage,
  b: HyDryEventMessage
): number {
  const eventDateDelta = b.messageDate.getTime() - a.messageDate.getTime();

  if (eventDateDelta !== 0) {
    return eventDateDelta;
  } else {
    return sortValueForHyDryEvent(b.event) - sortValueForHyDryEvent(a.event);
  }
}

export function compareHyDryEventLogs(
  a: HyDryEventLog,
  b: HyDryEventLog
): number {
  const eventDateDelta = b.eventDate.toMillis() - a.eventDate.toMillis();

  if (eventDateDelta !== 0 || !a.createdAt || !b.createdAt) {
    return eventDateDelta;
  } else {
    const createdAtDelta = b.createdAt.toMillis() - a.createdAt.toMillis();

    if (createdAtDelta === 0) {
      const bEvent = b.event ?? getHyDryEventFromEventNumber(b.eventNumber);
      const aEvent = a.event ?? getHyDryEventFromEventNumber(a.eventNumber);

      return sortValueForHyDryEvent(bEvent) - sortValueForHyDryEvent(aEvent);
    } else {
      return createdAtDelta;
    }
  }
}

/**
 * @param serialNumber the device the logs belong to. Needed because a Panel log stores a raw Panel
 *   event number, so the ComBox event vocabulary must not be applied to it: Panel event 60 is a
 *   power-usage fault, not `LID_CLOSED`.
 */
function convertSnapshots(snapshot: QuerySnapshot, serialNumber: string) {
  const logs = snapshot.docs.map((doc) => doc.data() as HyDryEventLog);

  const isPanel =
    getIresDeviceCategoryFromSerialNumber(serialNumber) ===
    IresDeviceCategory.PANEL;

  logs.forEach((log) => {
    log.eventCategory = resolveEffectiveHyDryEventCategory(
      log.eventNumber,
      log.eventCategory,
      log.additionalData,
      isPanel
    );

    log.event = getHyDryEventFromEventNumber(log.eventNumber);
  });

  return logs;
}

export async function saveHyDryEventLog(
  serialNumber: string,
  hyDryEventLog: HyDryEventLog
) {
  return getHyDryEventLogsRef(serialNumber)
    .doc(hyDryEventLog.id)
    .set(filterOutUndefined(hyDryEventLog));
}

export async function saveHyDryLogEventNote(
  logId: string,
  serialNumber: string,
  hyDryLogEventNote: HyDryEventLogNote
) {
  return getHyDryEventLogsRef(serialNumber)
    .doc(logId)
    .update({
      note: hyDryLogEventNote,
    });
}

export async function deleteHyDryEventLogNote(
  logId: string,
  serialNumber: string
) {
  return getHyDryEventLogsRef(serialNumber)
    .doc(logId)
    .delete();
}

export async function deleteHyDryEventLog(logId: string, serialNumber: string) {
  return getHyDryEventLogsRef(serialNumber)
    .doc(logId)
    .update({
      note: firestore.FieldValue.delete(),
    });
}

export function observeHyDryEventLogsAfter(
  serialNumber: string,
  date: Date,
  onNext: (logs: HyDryEventLog[]) => void
) {
  return getHyDryEventLogsRef(serialNumber)
    .where("eventDate", ">=", date)
    .orderBy("eventDate", "desc")
    .onSnapshot((snapshot) => onNext(convertSnapshots(snapshot, serialNumber)));
}

/**
 * Applies the newest-first ordering, so `limit` keeps the END of a chain rather than its start.
 *
 * Without an `orderBy` Firestore orders by the inequality field and then by document name. Every
 * log of a chain carries the SAME `dryRunStartDate` (it is the dry run's start, not the entry's
 * time), so that ordering is effectively arbitrary and the limit slices off whatever it reaches
 * first. Ordering has to begin with the inequality field, hence the pair.
 *
 * Requires the composite index `eventCategory ASC, dryRunStartDate DESC, eventDate DESC`.
 */
function orderNewestFirst(
  query: firestore.Query,
  rangeField: string
): firestore.Query {
  const ordered = query.orderBy(rangeField, "desc");

  return rangeField === "eventDate" ? ordered : ordered.orderBy("eventDate", "desc");
}

/**
 * Observes the event logs of one program chain.
 *
 * @param newestFirst Order newest-first so `limit` keeps the most recent entries. Off by default:
 *                    a caller whose limit is high enough to cover the whole chain gets the same
 *                    documents either way and needs no extra index. Turn it on where the limit can
 *                    actually bite and only recent entries matter, as the drying graph legend does
 *                    for the sensor snapshot it shows in the SENSOR INFO dialog.
 */
export function observeHyDryEventLogsOfProgramChain(
  programChain: ProgramChain,
  limit: number,
  categoriesToObserve: HyDryEventCategory[],
  onNext: (logs: HyDryEventLog[]) => void,
  isPanel?: boolean,
  newestFirst?: boolean
) {
  const rangeField = isPanel ? "eventDate" : "dryRunStartDate";

  let query = getHyDryEventLogsRef(programChain.serialNumber)
    .where("eventCategory", "in", categoriesToObserve)
    .where(rangeField, ">=", programChain.startDate) as firestore.Query;

  if (!doesProgramChainIncludeActiveRun(programChain)) {
    query = query.where(rangeField, "<=", getEndDateOfProgramChain(programChain));
  }

  if (newestFirst) {
    query = orderNewestFirst(query, rangeField);
  }

  return query.limit(limit).onSnapshot((snapshot) => {
    onNext(convertSnapshots(snapshot as QuerySnapshot, programChain.serialNumber));
  });
}

export function observeHyDryEventLogsInTimeRange(
  serialNumber: string,
  timeRangeStartDate: Date,
  timeRangeEndDate: Date | null,
  sortOrder: "asc" | "desc",
  onNext: (logs: HyDryEventLog[]) => void
) {
  if (timeRangeEndDate) {
    return getHyDryEventLogsRef(serialNumber)
      .where("eventDate", ">=", timeRangeStartDate)
      .where("eventDate", "<=", timeRangeEndDate)
      .orderBy("eventDate", sortOrder)
      .onSnapshot((snapshot) => {
        onNext(convertSnapshots(snapshot, serialNumber));
      });
  } else {
    return getHyDryEventLogsRef(serialNumber)
      .where("eventDate", ">=", timeRangeStartDate)
      .orderBy("eventDate", sortOrder)
      .onSnapshot((snapshot) => {
        onNext(convertSnapshots(snapshot, serialNumber));
      });
  }
}

export async function loadUnreadLogsOfCategory(
  serialNumber: string,
  info: UnreadHyDryEventMessagesInfo
): Promise<HyDryEventLog[]> {
  const limit = Math.min(info.numUnread, 50);

  if (limit === 0) {
    return [];
  }

  const result = await getHyDryEventLogsRef(serialNumber)
    .where("eventCategory", "==", info.eventCategory)
    .where("hasMessage", "==", true)
    .orderBy("eventDate", "desc")
    .limit(info.numUnread)
    .get();

  return result.docs.map((doc) => doc.data() as HyDryEventLog);
}

export async function loadUnreadLogsOfCategories(
  serialNumber: string,
  unread: UnreadHyDryEventMessagesInfo[]
) {
  const logs = await Promise.all(
    unread.map((info) => loadUnreadLogsOfCategory(serialNumber, info))
  );

  return logs
    .flatMap((list) => list)
    .sort((a, b) => compareHyDryEventLogs(a, b));
}
